import React, { useState, useEffect, useCallback } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  Layers,
  CheckCircle2,
  EyeOff,
  X,
  Image as ImageIcon,
  Video,
} from "lucide-react";
import {
  getAllFaqs,
  changeFaqStatus,
  createFaq,
  editFaq,
  deleteFaqApi,
} from "../../../api/faqs";
import { showToast } from "../../../utils/showToast";
import { apiBodyMessage } from "../../../utils/apiErrorMessage";
import ErrorMessage from "../../../components/errorMessage";
import { PageHeader } from "../../../components/ui/PageHeader";
import { Button } from "../../../components/ui/Button";
import { Drawer } from "../../../components/ui/Drawer";
import { Modal } from "../../../components/ui/Modal";
import { Input } from "../../../components/ui/Input";
import { Select } from "../../../components/ui/Select";
import { Switch } from "../../../components/ui/Switch";
import { FieldLabel } from "../../../components/ui/FieldLabel";
import "./faqModern.scss";
import { useTranslation } from "react-i18next";
import { formatNumberCL } from "../../../utils/formatters";
import { safeEmbedUrl, safeImageSrc } from "../../../utils/safeUrl";

const STATUS_CARDS_DEF = [
  { key: "all", labelKey: "Todas", icon: Layers, tone: "brand" },
  { key: "active", labelKey: "Activas", icon: CheckCircle2, tone: "success" },
  { key: "inactive", labelKey: "Inactivas", icon: EyeOff, tone: "neutral" },
];

const TEXTAREA_CLASS =
  "w-full rounded-lg border border-border bg-bg px-3 py-2 text-sm text-fg placeholder:text-fg-subtle focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent";

export default function ModernAdminFaqs() {
  const { t } = useTranslation();
  const STATUS_CARDS = STATUS_CARDS_DEF.map((c) => ({ ...c, label: t(c.labelKey) }));
  const methods = useForm({ mode: "onSubmit" });
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [update, setUpdate] = useState(true);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  // Drawer crear/editar (editingFaq === null → crear)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState(null);
  const [imageToUpload, setImageToUpload] = useState();

  // Modal eliminar
  const [deleteFaqTarget, setDeleteFaqTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const toggleFaq = (id) => setExpandedFaq(expandedFaq === id ? null : id);

  const changeStatus = async (id, value) => {
    try {
      const res = await changeFaqStatus({ id, status: value });
      // The api client never throws: it resolves { error: <body> }.
      if (res?.error) {
        showToast(apiBodyMessage(res.error) ?? t("Error al actualizar el estado"), false);
        return;
      }
      setUpdate(true);
      // Not res.data.message: the backend always answers in Spanish, so the
      // backend text would break the translation.
      showToast(t("Estado actualizado correctamente"), true);
    } catch {
      showToast(t("Error al actualizar el estado"), false);
    }
  };

  const toBase64 = (file) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => setImageToUpload(reader.result);
  };

  const openCreate = () => { setEditingFaq(null); setDrawerOpen(true); };
  const openEdit = (faq) => { setEditingFaq(faq); setDrawerOpen(true); };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingFaq(null);
    setImageToUpload(null);
    methods.reset();
  };

  const createValues = useCallback(
    async (values) => {
      const parsedValues = {
        title: values?.title,
        content: values?.content,
        videoUrl: values?.optionAditional === "video" ? values?.videoUrl : null,
        image: values?.optionAditional === "image" ? imageToUpload : null,
      };
      const created = await createFaq({ values: parsedValues });
      // On failure the drawer stays open so the operator does not lose the draft.
      if (created?.error) {
        showToast(apiBodyMessage(created.error) ?? t("No se pudo guardar la pregunta"), false);
        return;
      }
      showToast(created?.data?.message || t("Pregunta guardada correctamente"), true);
      setUpdate(true);
      closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imageToUpload]
  );

  const submitValues = useCallback(
    async (values) => {
      const parsedValues = {
        title: values?.title,
        content: values?.content,
        videoUrl: values?.optionAditional === "video" ? values?.videoUrl : null,
        image: values?.optionAditional === "image" ? imageToUpload : null,
        id: editingFaq?._id,
      };
      const edited = await editFaq({ values: parsedValues });
      // On failure the drawer stays open so the operator does not lose the draft.
      if (edited?.error) {
        showToast(apiBodyMessage(edited.error) ?? t("No se pudo guardar la pregunta"), false);
        return;
      }
      showToast(edited?.data?.message || t("Pregunta guardada correctamente"), true);
      setUpdate(true);
      closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editingFaq, imageToUpload]
  );

  // Prefill al abrir el drawer
  useEffect(() => {
    if (!drawerOpen) return;
    if (editingFaq) {
      methods.setValue("title", editingFaq?.title);
      methods.setValue("content", editingFaq?.content);
      if (editingFaq?.videoUrl) {
        methods.setValue("optionAditional", "video");
        methods.setValue("videoUrl", editingFaq?.videoUrl);
      } else if (editingFaq?.image) {
        methods.setValue("optionAditional", "image");
      } else {
        methods.setValue("optionAditional", "");
      }
    } else {
      methods.reset();
      setImageToUpload(null);
    }
  }, [drawerOpen, editingFaq, methods]);

  useEffect(() => {
    if (update) {
      setLoading(true);
      getAllFaqs()
        .then((data) => {
          // The list endpoint answers 404 ("No se encontraron preguntas.") when
          // there are no FAQs at all: that is the empty list, not a failure
          // (e.g. right after deleting the last one).
          if (data?.error?.statusCode === 404) {
            setFaqs([]);
            setFilteredFaqs([]);
            return;
          }
          // Any other `.error` (network/proxy/server failures) must not be read
          // as an empty list: the last known list stays on screen.
          if (data?.error) {
            showToast(
              apiBodyMessage(data.error) ?? t("No se pudieron cargar las preguntas frecuentes"),
              false
            );
            return;
          }
          setFaqs(data?.data?.faqs || []);
          setFilteredFaqs(data?.data?.faqs || []);
        })
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
      setUpdate(false);
    }
  }, [update, t]);

  // Filtro por estado + búsqueda
  useEffect(() => {
    let filtered = faqs;
    if (filterStatus === "active") filtered = filtered.filter((f) => f.isActive);
    else if (filterStatus === "inactive") filtered = filtered.filter((f) => !f.isActive);
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (f) => f.title.toLowerCase().includes(q) || f.content.toLowerCase().includes(q)
      );
    }
    setFilteredFaqs(filtered);
  }, [searchTerm, filterStatus, faqs]);

  const confirmDelete = async () => {
    // A second click while the first request is in flight would send a second
    // DELETE (the row is already gone by then, so it returns 404).
    if (deleting) return;
    setDeleting(true);
    try {
      const deleted = await deleteFaqApi({ id: deleteFaqTarget?._id });
      // On failure the modal stays open: the operator sees what could not be deleted.
      if (deleted?.error) {
        showToast(apiBodyMessage(deleted.error) ?? t("No se pudo eliminar la pregunta"), false);
        return;
      }
      setDeleteFaqTarget(null);
      setUpdate(true);
      showToast(deleted?.data?.message || t("Pregunta eliminada correctamente"), true);
    } finally {
      setDeleting(false);
    }
  };

  const counts = {
    all: faqs.length,
    active: faqs.filter((f) => f.isActive).length,
    inactive: faqs.filter((f) => !f.isActive).length,
  };

  const optionAditional = methods.watch("optionAditional");

  // La vista previa del drawer también pasa por el filtro: `imageToUpload` es un data URL
  // propio (FileReader), pero `editingFaq.image` viene de la API.
  const previewImageSrc = safeImageSrc(imageToUpload || editingFaq?.image);

  return (
    <div className="w-full min-w-0 faq">
      {/* ── Header ── */}
      <PageHeader
        icon={<HelpCircle className="w-6 h-6 text-fg-muted" />}
        title={t("Preguntas frecuentes")}
        subtitle={t("Gestiona las preguntas frecuentes que verán tus clientes.")}
        actions={
          <Button onClick={openCreate} iconLeft={<Plus size={16} />}>
            {t("Nueva pregunta")}
          </Button>
        }
      />

      {/* ── Cards de filtro por estado ── */}
      <div className="faq-kpis">
        {STATUS_CARDS.map((c) => {
          const Icon = c.icon;
          const isActive = filterStatus === c.key;
          return (
            <button key={c.key} className={`faq-kpi faq-kpi--${c.tone} ${isActive ? "is-active" : ""}`} onClick={() => setFilterStatus(c.key)}>
              <div className="faq-kpi__top">
                <span className="faq-kpi__icon"><Icon size={16} /></span>
                <span className="faq-kpi__count">{formatNumberCL(counts[c.key])}</span>
              </div>
              <div className="faq-kpi__label">{c.label}</div>
            </button>
          );
        })}
      </div>

      {/* ── Búsqueda ── */}
      <div className="faq-searchrow">
        <div className="faq-search">
          <Search size={16} className="faq-search__icon" />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t("Buscar por título o contenido…")}
          />
        </div>
        {(searchTerm || filterStatus !== "all") && (
          <Button
            variant="ghost"
            iconLeft={<X size={16} />}
            onClick={() => { setSearchTerm(""); setFilterStatus("all"); }}
          >
            {t("Limpiar")}
          </Button>
        )}
      </div>

      {/* ── Lista (acordeón moderno) ── */}
      <div className="faq-card">
        {loading ? (
          <div className="faq-skel">
            {[0, 1, 2, 3, 4].map((i) => <div key={i} className="faq-skel__row" />)}
          </div>
        ) : filteredFaqs.length === 0 ? (
          <div className="faq-empty">
            <HelpCircle size={28} />
            <p className="faq-empty__title">
              {searchTerm || filterStatus !== "all" ? t("Sin resultados") : t("No hay preguntas frecuentes")}
            </p>
            <p className="faq-empty__desc">
              {searchTerm || filterStatus !== "all" ? t("Ajusta los filtros de búsqueda.") : t("Crea tu primera pregunta frecuente.")}
            </p>
          </div>
        ) : (
          <ul className="faq-list">
            {filteredFaqs.map((faq) => {
              const isOpen = expandedFaq === faq._id;
              // `videoUrl` es texto libre de este mismo formulario: si no pasa el filtro
              // de esquema no se embebe. Un `javascript:` en el src del iframe correría
              // con el origen del panel y el accessToken del operador a mano.
              const imageSrc = safeImageSrc(faq.image);
              const videoSrc = safeEmbedUrl(faq.videoUrl);
              return (
                <li key={faq._id} className={`faq-item ${isOpen ? "is-open" : ""} ${!faq.isActive ? "is-inactive" : ""}`}>
                  <div className="faq-item__head" onClick={() => toggleFaq(faq._id)}>
                    <span className="faq-item__chev">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</span>
                    <span className="faq-item__title">{faq.title}</span>
                    {faq.videoUrl && <span className="faq-item__media" title={t("Tiene video")}><Video size={14} /></span>}
                    {faq.image && <span className="faq-item__media" title={t("Tiene imagen")}><ImageIcon size={14} /></span>}
                    <span className={`faq-badge faq-badge--${faq.isActive ? "success" : "neutral"} faq-badge--dot`}>
                      {faq.isActive ? t("Activa") : t("Inactiva")}
                    </span>
                    <div className="faq-item__actions" onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={faq.isActive}
                        onChange={(next) => changeStatus(faq._id, next)}
                        size="sm"
                        ariaLabel={faq.isActive ? t("Visible") : t("Oculta")}
                      />
                      <button className="faq-iconbtn" title={t("Editar")} onClick={() => openEdit(faq)}><Edit size={15} /></button>
                      <button className="faq-iconbtn faq-iconbtn--danger" title={t("Eliminar")} onClick={() => setDeleteFaqTarget(faq)}><Trash2 size={15} /></button>
                    </div>
                  </div>
                  {isOpen && (
                    <div className="faq-item__body">
                      <p className="faq-item__content">{faq.content}</p>
                      {imageSrc && <img src={imageSrc} alt={t("Ilustración")} className="faq-item__image dark-safe-img" />}
                      {videoSrc && (
                        <iframe
                          src={videoSrc} title="Video"
                          frameBorder="0" allowFullScreen className="faq-item__video"
                          // Mismo sandbox que el gemelo del portal. Lo que protege es el
                          // filtro de esquema (con `allow-same-origin` el sandbox no aísla
                          // un documento del propio origen); esto solo recorta permisos
                          // del reproductor externo.
                          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                          allow="encrypted-media; picture-in-picture; fullscreen"
                        />
                      )}
                      {faq?.videoUrl && !videoSrc && (
                        <p className="faq-item__content">
                          {t("El enlace del video no es una URL https válida: no se puede mostrar.")}
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/* ── Drawer crear / editar (lateral derecho) ── */}
      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        size="lg"
        title={editingFaq ? t("Editar pregunta") : t("Nueva pregunta")}
        footer={
          <>
            <Button variant="ghost" onClick={closeDrawer}>{t("Cancelar")}</Button>
            <Button type="submit" form="faq-form">
              {editingFaq ? t("Guardar cambios") : t("Crear pregunta")}
            </Button>
          </>
        }
      >
        <form
          id="faq-form"
          className="flex flex-col gap-4"
          onSubmit={methods.handleSubmit(editingFaq ? submitValues : createValues)}
        >
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="faq-title">{t("Título de la pregunta")}</FieldLabel>
            <Controller
              name="title"
              control={methods.control}
              rules={{ required: t("Ingrese un título") }}
              render={({ field }) => (
                <Input {...field} id="faq-title" type="text" placeholder={t("¿Cómo funciona el servicio de delivery?")} />
              )}
            />
            {methods.formState.errors?.title && <ErrorMessage msg={methods.formState.errors?.title?.message} />}
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="faq-content">{t("Respuesta")}</FieldLabel>
            <Controller
              name="content"
              control={methods.control}
              rules={{ required: t("Ingrese una respuesta") }}
              render={({ field }) => (
                <textarea {...field} id="faq-content" rows={6} className={TEXTAREA_CLASS} placeholder={t("Escribe la respuesta detallada aquí…")} />
              )}
            />
            {methods.formState.errors?.content && <ErrorMessage msg={methods.formState.errors?.content?.message} />}
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="faq-option">{t("Contenido adicional (opcional)")}</FieldLabel>
            <Controller
              name="optionAditional"
              control={methods.control}
              render={({ field }) => (
                <Select {...field} id="faq-option">
                  <option value="">{t("Ninguno")}</option>
                  <option value="video">{t("Video (YouTube)")}</option>
                  <option value="image">{t("Imagen")}</option>
                </Select>
              )}
            />
          </div>

          {optionAditional === "video" && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="faq-video">{t("URL del video")}</FieldLabel>
              <Controller
                name="videoUrl"
                control={methods.control}
                rules={{
                  // Aviso al operador, NO el control de seguridad: quien tenga el token del
                  // panel puede llamar la API directo. El control real es el filtro del
                  // render (y el backend, si algún día se puede tocar). Solo se valida lo
                  // que el operador TECLEA: una FAQ vieja con un enlace inválido se puede
                  // seguir editando (el render ya no lo embebe).
                  validate: (value) =>
                    !value ||
                    value === (editingFaq?.videoUrl ?? "") ||
                    safeEmbedUrl(value) !== null ||
                    t("Pega la URL https del video (por ejemplo https://www.youtube.com/embed/…)"),
                }}
                render={({ field }) => (
                  <Input {...field} id="faq-video" type="text" placeholder="https://www.youtube.com/embed/…" />
                )}
              />
              {methods.formState.errors?.videoUrl && (
                <ErrorMessage msg={methods.formState.errors?.videoUrl?.message} />
              )}
            </div>
          )}

          {optionAditional === "image" && (
            <div className="flex flex-col gap-1.5">
              <FieldLabel htmlFor="faq-image">{t("Seleccionar imagen")}</FieldLabel>
              <input
                id="faq-image"
                type="file"
                accept="image/*"
                onChange={(e) => toBase64(e.target.files[0])}
                className="block w-full text-sm text-fg-muted file:mr-3 file:rounded-md file:border-0 file:bg-brand-500 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-brand-600"
              />
              {previewImageSrc && (
                <div className="mt-2 overflow-hidden rounded-lg border border-border">
                  <img src={previewImageSrc} alt="Preview" className="block w-full dark-safe-img" />
                </div>
              )}
            </div>
          )}
        </form>
      </Drawer>

      {/* ── Modal eliminar (confirmación) ── */}
      <Modal
        open={!!deleteFaqTarget}
        onClose={() => setDeleteFaqTarget(null)}
        size="sm"
        title={t("Confirmar eliminación")}
        footer={
          <>
            <Button variant="outline" onClick={() => setDeleteFaqTarget(null)}>{t("Cancelar")}</Button>
            <Button variant="danger" onClick={confirmDelete} loading={deleting} iconLeft={<Trash2 size={15} />}>{t("Eliminar")}</Button>
          </>
        }
      >
        <p className="text-sm text-fg">{t("¿Seguro que deseas eliminar esta pregunta frecuente?")}</p>
        {deleteFaqTarget && (
          <div className="mt-3 rounded-lg border border-border bg-bg-muted p-3">
            <strong className="block text-sm font-semibold text-fg">{deleteFaqTarget.title}</strong>
            <p className="mt-1 text-sm text-fg-muted line-clamp-3">{deleteFaqTarget.content}</p>
          </div>
        )}
      </Modal>
    </div>
  );
}
